import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  Pressable,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch } from "react-redux";

import ExchangeIcon from "@/assets/exchangeicon.svg";
import ReceiptModal from "@/components/modal/banking/ReceiptModal";
import { Button, ButtonText } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import {
  Modal,
  ModalBackdrop,
  ModalCloseButton,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { sendRequest } from "@/utils/ApiUtil";
import { checkAssetPrecision } from "@/utils/transactions/ExchangeCurrencies";
import { checkMobileTransferIcon } from "@/utils/transactions/Icons";
import {
  checkTransferType,
  checkTransactionSenderReceiver,
  checkTransactionValue,
  checkTransactionStatus,
  checkTransactionDate,
} from "@/utils/transactions/TransactionsUtils";

export const TransactionsLine = (props: any) => {
  const [assetDecimalPrecision, setAssetDecimalPrecision] = useState(2);
  const [showDetails, setShowDetails] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLg, setIsLg] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);
  const blockedTransactionTypeReceipts = ["OTC_INTERNAL_TRANSFER"];

  useEffect(() => {
    const updateLayout = () => {
      const screenDimensions = Dimensions.get("window");
      setIsLg(screenDimensions.width > 1024);
    };

    updateLayout();

    const subscription = Dimensions.addEventListener("change", updateLayout);

    return () => subscription.remove();
  }, []);

  useEffect(() => {
    const currentAccount = globalValues.accountsData.find(
      (account: any) => account.id === globalValues.currentAccountId
    );
    setAssetDecimalPrecision(
      checkAssetPrecision(currentAccount?.assetTypeId ?? 2)
    );
  }, [globalValues.currentAccountId]);

  const confirmScheduleTransaction = async (id: string, action: string) => {
    setIsLoading(true);
    try {
      const method = action === "ACCEPT" ? "POST" : "DELETE";
      const message =
        action === "ACCEPT"
          ? t("common:toast.subtitle.transactionapproved")
          : t("common:toast.subtitle.transactionrejected");

      const url =
        action === "ACCEPT" ? "/withdrawal/accept" : "/withdrawal/cancel";

      const request = await sendRequest(method, url, { id }, true, true);
      if (request) {
        setIsLoading(false);
        dispatch(setForm({ transactionsHasChanges: true }));
        Toast.show({
          type: "success",
          visibilityTime: 5000,
          props: {
            text1: t("common:toast.success"),
            text2: message,
          },
        });
        setShowModal(false);
      }
    } catch (error: any) {
      console.log(error);
      setIsLoading(false);
      setShowModal(false);
    }
  };

  function checkTransferIcon(type: string) {
    switch (type) {
      case "FIAT_TRANSFER_IN":
        return (
          <AntDesign name="arrowdown" size={isLg ? 22 : 16} color="#BDBDBD" />
        );
      case "FIAT_TRANSFER_OUT":
        return (
          <AntDesign name="arrowup" size={isLg ? 22 : 16} color="#BDBDBD" />
        );
      case "TRADE":
        return (
          <ExchangeIcon
            style={{ width: isLg ? 24 : 16, height: isLg ? 24 : 16 }}
          />
        );
      default:
        return (
          <AntDesign name="arrowup" size={isLg ? 22 : 16} color="#BDBDBD" />
        );
    }
  }

  function checkStatusColor(status: string, isText: boolean = false) {
    switch (status) {
      case "EXECUTED":
        return isText ? "text-greenstatus" : "bg-greenstatus";
      case "PAID":
        return isText ? "text-greenstatus" : "bg-greenstatus";
      case "POSTED":
        return isText ? "text-greenstatus" : "bg-greenstatus";
      case "CANCELLED":
        return isText ? "text-redstatus" : "bg-redstatus";
      case "REJECTED":
        return isText ? "text-redstatus" : "bg-redstatus";
      case "FAILED":
        return isText ? "text-redstatus" : "bg-redstatus";
      case "SCHEDULED":
        return isText ? "text-bluestatus" : "bg-bluestatus";
      default:
        return isText ? "text-yellowstatus" : "bg-yellowstatus";
    }
  }

  function checkStatusIcon(status: string, size: number = 14) {
    switch (status) {
      case "EXECUTED":
        return <AntDesign name="check" size={size} className="text-white1" />;
      case "PAID":
        return <AntDesign name="check" size={size} className="text-white1" />;
      case "POSTED":
        return <AntDesign name="check" size={size} className="text-white1" />;
      case "CANCELLED":
        return <AntDesign name="close" size={size} className="text-white1" />;
      case "REJECTED":
        return <AntDesign name="close" size={size} className="text-white1" />;
      case "FAILED":
        return <AntDesign name="close" size={size} className="text-white1" />;
      case "SCHEDULED":
        return (
          <AntDesign name="clockcircleo" size={size} className="text-white1" />
        );
      default:
        return (
          <AntDesign name="hourglass" size={size} className="text-white1" />
        );
    }
  }

  const status = props?.transactionStatus || props?.status;

  return (
    <Pressable
      className="flex-row items-center rounded-lg transition-color delay-0 md:bg-gray md:border md:border-lightgray w-full py-2 md:my-1"
      disabled={status !== "SCHEDULED"}
      onPress={() => setShowModal(true)}
    >
      <View className="flex-row justify-between pb-1 border-b border-lightgray w-full md:hidden">
        <View className="flex-row items-center gap-1 w-1/3">
          {checkMobileTransferIcon(props.transactionType)}
          <View className="flex-col">
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              className="text-base max-w-24"
            >
              {checkTransactionSenderReceiver(
                props,
                globalValues.currentAccountId,
                true
              )}
            </Text>
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              className="font-light text-sm"
            >
              {checkTransactionDate(props)}
            </Text>
          </View>
        </View>
        <View className="flex-row justify-end gap-3 w-2/3">
          <View className="flex-col items-center">
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              className="text-base font-medium max-w-24"
            >
              {checkTransactionValue(
                props,
                globalValues.currentAccountId,
                props.isFiat,
                props.currentAsset,
                assetDecimalPrecision
              )}
            </Text>
            <View className="flex-row rounded-full gap-1.5 px-4 py-1 items-center justify-center min-w-32">
              <Text
                numberOfLines={1}
                ellipsizeMode="tail"
                className={`text-sm text-center ${checkStatusColor(
                  status,
                  true
                )}`}
              >
                {checkTransactionStatus(props, t)}
              </Text>
            </View>
          </View>
          <Pressable
            className="items-center justify-center"
            onPress={() => setShowDetails(true)}
            disabled={blockedTransactionTypeReceipts.includes(
              props.transactionType
            )}
          >
            <MaterialIcons name="arrow-outward" size={22} color="#BDBDBD" />
            <Text className="text-xs text-midgray">
              {t("common:terms.receipt")}
            </Text>
          </Pressable>
        </View>
      </View>
      <View className="w-1/6 h-fit items-center flex-row px-1 lg:mx-0 hidden md:flex">
        <View className="items-center w-1/6">
          {checkTransferIcon(props.transactionType)}
        </View>
        <View className="py-1 px-2 flex-wrap w-5/6 max-w-12 lg:max-w-none lg:p-0 lg:mx-2">
          <Text
            numberOfLines={2}
            ellipsizeMode="tail"
            className="text-xs font-light lg:text-base"
          >
            {(props.transactionStatus || props.status) !== "SCHEDULED"
              ? checkTransactionDate(props)
              : ""}
          </Text>
        </View>
      </View>
      <View className="p-1 w-1/6 items-center justify-center lg:mx-0 lg:p-0 hidden md:flex">
        <Text
          numberOfLines={1}
          ellipsizeMode="middle"
          className="text-xs font-light text-center lg:text-base"
        >
          {checkTransactionSenderReceiver(
            props,
            globalValues.currentAccountId,
            true
          )}
        </Text>
      </View>
      <View className="p-1 w-1/6 items-center justify-center lg:mx-0 lg:p-0 hidden md:flex">
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          className="text-xs font-light text-center lg:text-base"
        >
          {checkTransferType(
            props.transactionType,
            props.type ?? "-",
            props.rail ?? "-",
            t
          )}
        </Text>
      </View>
      <View className="w-1/6 flex-row items-center justify-center lg:mx-0 hidden md:flex">
        <Text className="text-xs font-light text-center lg:text-base">
          {checkTransactionValue(
            props,
            globalValues.currentAccountId,
            props.isFiat,
            props.currentAsset,
            assetDecimalPrecision
          )}
        </Text>
      </View>
      <View className="w-1/6 justify-center items-center lg:mx-0 hidden md:flex">
        <View
          className={`flex-row rounded-full gap-1.5 px-4 py-1 items-center justify-center min-w-36 min-h-6 max-h-6 ${checkStatusColor(
            status
          )}`}
        >
          <Text
            numberOfLines={1}
            ellipsizeMode="tail"
            className="text-sm text-white1 font-light text-center"
          >
            {checkTransactionStatus(props, t)}
          </Text>
          {checkStatusIcon(status)}
        </View>
      </View>
      <View className="w-1/6 justify-center items-center lg:'mx-0 hidden md:flex">
        <Pressable
          onPress={() => setShowDetails(true)}
          disabled={blockedTransactionTypeReceipts.includes(
            props.transactionType
          )}
        >
          <AntDesign
            name="jpgfile1"
            size={24}
            className="text-midgray hover:text-darkergray"
          />
        </Pressable>
      </View>
      <ReceiptModal
        visible={showDetails}
        details={props}
        onClose={() => setShowDetails(false)}
        onClick={(state) => setShowDetails(state)}
      />
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
      >
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="lg">
              {t("home/home:transaction.modal.title")}
            </Heading>
            <ModalCloseButton>
              <AntDesign name="close" size={24} color="black" />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text className="text-base">{`${t(
              "home/home:transaction.modal.content"
            )} ${checkTransactionValue(
              props,
              globalValues.currentAccountId,
              props.isFiat,
              props.currentAsset,
              assetDecimalPrecision
            )} to ${checkTransactionSenderReceiver(
              props,
              globalValues.currentAccountId,
              true
            )}`}</Text>
          </ModalBody>
          <ModalFooter>
            {isLoading ? (
              <></>
            ) : (
              <Button
                variant="solid"
                size="sm"
                action="secondary"
                className="bg-red mr-2"
                style={{ alignItems: "center" }}
                onPress={() => confirmScheduleTransaction(props.id, "DECLINE")}
              >
                <ButtonText className="text-white1">
                  {t("common:terms.decline")}
                </ButtonText>
                <AntDesign
                  className="ml-2"
                  name="close"
                  size={16}
                  color="white"
                />
              </Button>
            )}
            {isLoading ? (
              <View>
                <ActivityIndicator
                  size="small"
                  color="#5D0CBA"
                  className="mx-2"
                />
              </View>
            ) : (
              <Button
                variant="solid"
                size="sm"
                className="bg-greenstatus mr-2"
                action="primary"
                onPress={() => confirmScheduleTransaction(props.id, "ACCEPT")}
              >
                <ButtonText className="text-white1">
                  {t("common:terms.accept")}
                </ButtonText>
                <AntDesign
                  className="ml-2"
                  name="check"
                  size={16}
                  color="white"
                />
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Pressable>
  );
};
